import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@aimsos/hooks';
import {
  deleteDocument,
  unassignAgentFromDocument,
  unassignAgentFromVault,
} from '../../../../services';
import { useAgentKnowledgeEnv } from '../../context/knowledge/AgentKnowledgeEnvContext.jsx';
import { useAgentKnowledgeDrives } from '../../context/knowledge/AgentKnowledgeDrivesContext.jsx';
import { useAgentKnowledgeDocuments } from '../../context/knowledge/AgentKnowledgeDocumentsContext.jsx';

/**
 * Knowledge tab: drives + cross-vault agent docs (tab 0) vs vault-only docs (tab 1),
 * debounced local search (700ms), remove drive / remove access / delete vault doc.
 */
export function useAgentKnowledgePage({ t, toast }) {
  const { agentId } = useAgentKnowledgeEnv();
  const {
    drives,
    agentDocuments,
    loading: drivesLoading,
    error: drivesError,
    hasFetched: drivesFetched,
    fetchAll: fetchDrivesAndAgentDocs,
    drivesCount,
    agentDocumentsCount,
    removeDrive,
    removeAgentDocument,
  } = useAgentKnowledgeDrives();

  const {
    documents: vaultDocuments,
    loading: vaultDocumentsLoading,
    error: vaultDocumentsError,
    hasFetched: vaultDocumentsFetched,
    fetchDocuments: fetchVaultDocuments,
    documentsCount: vaultDocumentsCount,
    removeDocument: removeVaultDocument,
    vaultUuid,
  } = useAgentKnowledgeDocuments();

  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 700);

  const [removingDocumentIds, setRemovingDocumentIds] = useState(() => new Set());
  const [removingDriveIds, setRemovingDriveIds] = useState(() => new Set());
  const [deletingDocumentIds, setDeletingDocumentIds] = useState(() => new Set());

  const [showRemoveDriveModal, setShowRemoveDriveModal] = useState(false);
  const [showRemoveDocumentModal, setShowRemoveDocumentModal] = useState(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  useEffect(() => {
    if (!drivesFetched) {
      void fetchDrivesAndAgentDocs();
    }
  }, [drivesFetched, fetchDrivesAndAgentDocs]);

  useEffect(() => {
    if (activeTab === 1 && vaultUuid && !vaultDocumentsFetched) {
      void fetchVaultDocuments();
    }
  }, [activeTab, vaultUuid, vaultDocumentsFetched, fetchVaultDocuments]);

  const loading = activeTab === 0 ? drivesLoading : vaultDocumentsLoading;
  const error = activeTab === 0 ? drivesError : vaultDocumentsError;

  const filteredDrives = useMemo(() => {
    if (!debouncedSearchTerm?.trim()) return drives;
    const q = debouncedSearchTerm.toLowerCase();
    return drives.filter(
      (drive) =>
        drive.name?.toLowerCase().includes(q) ||
        String(drive.vaultUuid || '').toLowerCase().includes(q) ||
        String(drive.uuid || '').toLowerCase().includes(q),
    );
  }, [drives, debouncedSearchTerm]);

  const filteredAgentDocuments = useMemo(() => {
    if (!debouncedSearchTerm?.trim()) return agentDocuments;
    const q = debouncedSearchTerm.toLowerCase();
    return agentDocuments.filter(
      (doc) =>
        doc.name?.toLowerCase().includes(q) ||
        doc.type?.toLowerCase().includes(q) ||
        String(doc.uuid || '').toLowerCase().includes(q) ||
        String(doc.metadata?.source || '').toLowerCase().includes(q),
    );
  }, [agentDocuments, debouncedSearchTerm]);

  const filteredVaultDocuments = useMemo(() => {
    if (!debouncedSearchTerm?.trim()) return vaultDocuments;
    const q = debouncedSearchTerm.toLowerCase();
    return vaultDocuments.filter(
      (doc) =>
        doc.name?.toLowerCase().includes(q) ||
        doc.type?.toLowerCase().includes(q) ||
        String(doc.uuid || '').toLowerCase().includes(q) ||
        String(doc.metadata?.source || '').toLowerCase().includes(q),
    );
  }, [vaultDocuments, debouncedSearchTerm]);

  const totalDrivesTabItems = filteredDrives.length + filteredAgentDocuments.length;
  const totalDocumentsTabItems = filteredVaultDocuments.length;
  const totalItems = activeTab === 0 ? totalDrivesTabItems : totalDocumentsTabItems;

  useEffect(() => {
    setSearchTerm('');
  }, [activeTab]);

  const handleTabChange = useCallback((index) => {
    setActiveTab(index);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleRemoveDriveAccess = useCallback((drive) => {
    setItemToRemove(drive);
    setShowRemoveDriveModal(true);
  }, []);

  const handleConfirmRemoveDrive = useCallback(async () => {
    if (!itemToRemove || !agentId) return;
    const driveId = itemToRemove.vaultUuid || itemToRemove.uuid || itemToRemove.id;
    setRemovingDriveIds((prev) => new Set([...prev, driveId]));
    try {
      await unassignAgentFromVault(driveId, agentId);
      removeDrive(driveId);
      toast.success({ title: t('edit.knowledge.toastDriveRemoved'), canClose: true });
    } catch (err) {
      toast.error({
        title: t('edit.knowledge.toastErrorTitle'),
        message: err?.message ?? t('edit.knowledge.toastDriveRemoveFailed'),
        canClose: true,
      });
    } finally {
      setRemovingDriveIds((prev) => {
        const next = new Set(prev);
        next.delete(driveId);
        return next;
      });
      setShowRemoveDriveModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, removeDrive, toast, t]);

  const handleDeleteAgentDocument = useCallback((document) => {
    setItemToRemove(document);
    setShowRemoveDocumentModal(true);
  }, []);

  const handleConfirmRemoveDocument = useCallback(async () => {
    if (!itemToRemove || !agentId) return;
    const documentId = itemToRemove.uuid || itemToRemove.id;
    setRemovingDocumentIds((prev) => new Set([...prev, documentId]));
    try {
      await unassignAgentFromDocument(documentId, agentId);
      removeAgentDocument(documentId);
      toast.success({ title: t('edit.knowledge.toastDocAccessRemoved'), canClose: true });
    } catch (err) {
      toast.error({
        title: t('edit.knowledge.toastErrorTitle'),
        message: err?.message ?? t('edit.knowledge.toastDocAccessFailed'),
        canClose: true,
      });
    } finally {
      setRemovingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setShowRemoveDocumentModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, removeAgentDocument, toast, t]);

  const handleDeleteVaultDocument = useCallback((document) => {
    setItemToRemove(document);
    setShowDeleteDocumentModal(true);
  }, []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    if (!itemToRemove || !agentId) return;
    const documentId = itemToRemove.uuid || itemToRemove.id;
    setDeletingDocumentIds((prev) => new Set([...prev, documentId]));
    try {
      await unassignAgentFromDocument(documentId, agentId);
      await deleteDocument({ document_uuid: documentId });
      removeVaultDocument(documentId);
      toast.success({ title: t('edit.knowledge.toastDocDeleted'), canClose: true });
    } catch (err) {
      toast.error({
        title: t('edit.knowledge.toastErrorTitle'),
        message: err?.message ?? t('edit.knowledge.toastDocDeleteFailed'),
        canClose: true,
      });
    } finally {
      setDeletingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setShowDeleteDocumentModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, removeVaultDocument, toast, t]);

  const handleCloseRemoveDriveModal = useCallback(() => {
    setShowRemoveDriveModal(false);
    setItemToRemove(null);
  }, []);

  const handleCloseRemoveDocumentModal = useCallback(() => {
    setShowRemoveDocumentModal(false);
    setItemToRemove(null);
  }, []);

  const handleCloseDeleteDocumentModal = useCallback(() => {
    setShowDeleteDocumentModal(false);
    setItemToRemove(null);
  }, []);

  return {
    activeTab,
    viewMode,
    searchTerm,
    drives: filteredDrives,
    agentDocuments: filteredAgentDocuments,
    vaultDocuments: filteredVaultDocuments,
    loading,
    error,
    totalItems,
    totalDrivesTabItems,
    totalDocumentsTabItems,
    drivesCount,
    agentDocumentsCount,
    vaultDocumentsCount,
    vaultUuid,
    handleTabChange,
    handleViewModeChange,
    handleSearchChange,
    handleRemoveDriveAccess,
    handleDeleteAgentDocument,
    handleDeleteVaultDocument,
    removingDocumentIds,
    removingDriveIds,
    deletingDocumentIds,
    showRemoveDriveModal,
    showRemoveDocumentModal,
    showDeleteDocumentModal,
    itemToRemove,
    handleConfirmRemoveDrive,
    handleConfirmRemoveDocument,
    handleConfirmDeleteDocument,
    handleCloseRemoveDriveModal,
    handleCloseRemoveDocumentModal,
    handleCloseDeleteDocumentModal,
  };
}
